import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Download, FileText, Send, Package } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    let currentProduct = null;
    axios.get(`${API}/products/${id}`)
      .then(res => {
        currentProduct = res.data;
        setProduct(res.data);
        return axios.get(`${API}/products`);
      })
      .then(res => {
        const allProducts = res.data;
        const sameCategory = allProducts.filter(p => p.id !== id && p.category === currentProduct?.category);
        if (sameCategory.length >= 2) {
          setRelatedProducts(sameCategory.slice(0, 3));
        } else {
          setRelatedProducts(allProducts.filter(p => p.id !== id).slice(0, 3));
        }
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB]">
        <div className="w-8 h-8 border-2 border-[#C5A55A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-500 hover:text-[#C5A55A] transition-colors text-sm"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('nav.home')} / {t('nav.products')} / {product.name}
        </button>
      </div>

      {/* Product Main */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Image */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <div className="relative bg-[#F4F7FB] p-8 flex items-center justify-center min-h-[400px]">
              <img
                src={product.image_url}
                alt={product.name}
                className="max-w-full max-h-[450px] object-contain"
                data-testid="product-detail-image"
              />
              {product.in_stock && (
                <div className="absolute top-4 right-4 flex items-center gap-1 bg-emerald-500 text-white text-xs font-semibold px-3 py-1">
                  <CheckCircle className="w-3 h-3" />
                  {t('products.inStock')}
                </div>
              )}
              <div className="absolute top-4 left-4 bg-[#0A1628] text-[#C5A55A] text-xs font-mono px-3 py-1 tracking-wider uppercase">
                {product.category}
              </div>
            </div>
          </motion.div>

          {/* Info */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.15 }}>
            <p className="text-[#C5A55A] text-sm font-mono tracking-[0.2em] uppercase mb-3">
              {product.category.replace(/-/g, ' ')}
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0A1628] mb-6" data-testid="product-detail-name">
              {product.name}
            </h1>
            <p className="text-slate-600 text-base leading-relaxed mb-8" data-testid="product-detail-description">
              {product.description}
            </p>

            {product.sizes && (
              <div className="mb-8">
                <h3 className="font-heading text-sm font-semibold text-[#0A1628] uppercase tracking-wider mb-3">
                  {t('products.availableSizes')}
                </h3>
                <p className="text-slate-600 text-sm bg-[#F4F7FB] px-4 py-3" data-testid="product-detail-sizes">
                  {product.sizes}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-4 mb-10">
              <a
                href={product.catalog_pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#0A1628] text-white font-semibold px-6 py-3 hover:bg-[#142240] transition-all duration-300 flex items-center gap-2 text-sm tracking-wider uppercase"
                data-testid="product-download-catalog-btn"
              >
                <Download className="w-4 h-4" />
                {t('products.catalogPdf')}
              </a>
              <button
                onClick={() => navigate('/#contact')}
                className="bg-[#C5A55A] text-[#0A1628] font-semibold px-6 py-3 hover:bg-[#D7C085] transition-all duration-300 flex items-center gap-2 text-sm tracking-wider uppercase"
                data-testid="product-request-quote-btn"
              >
                <Send className="w-4 h-4" />
                {t('hero.requestQuote')}
              </button>
            </div>

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div className="mb-10">
                <h3 className="font-heading text-sm font-semibold text-[#0A1628] uppercase tracking-wider mb-4">
                  {t('products.keyFeatures')}
                </h3>
                <ul className="space-y-3">
                  {product.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`product-feature-${i}`}>
                      <CheckCircle className="w-4 h-4 text-[#C5A55A] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </div>

        {/* Specifications Table */}
        {product.specs && Object.keys(product.specs).length > 0 && (
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="mt-20"
          >
            <h2 className="font-heading text-2xl font-semibold text-[#0A1628] mb-8 flex items-center gap-3">
              <FileText className="w-6 h-6 text-[#C5A55A]" />
              {t('products.techSpecs')}
            </h2>
            <div className="border border-slate-200 overflow-hidden" data-testid="product-specs-table">
              {Object.entries(product.specs).map(([key, value], i) => (
                <div key={key} className={`grid grid-cols-3 ${i % 2 === 0 ? 'bg-[#F4F7FB]' : 'bg-white'}`}>
                  <div className="col-span-1 px-6 py-4 font-medium text-[#0A1628] text-sm border-r border-slate-200">
                    {key}
                  </div>
                  <div className="col-span-2 px-6 py-4 text-slate-600 text-sm">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="mt-20"
          >
            <h2 className="font-heading text-2xl font-semibold text-[#0A1628] mb-8 flex items-center gap-3">
              <Package className="w-6 h-6 text-[#C5A55A]" />
              {t('products.relatedProducts')}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedProducts.map((rp) => (
                <div
                  key={rp.id}
                  onClick={() => navigate(`/product/${rp.id}`)}
                  className="group bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:border-[#C5A55A] transition-all duration-500 cursor-pointer"
                  data-testid={`related-product-${rp.id}`}
                >
                  <div className="relative overflow-hidden h-48">
                    <img
                      src={rp.image_url}
                      alt={rp.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-lg font-semibold text-[#0A1628] group-hover:text-[#C5A55A] transition-colors">
                      {rp.name}
                    </h3>
                    <p className="text-slate-500 text-sm mt-2 line-clamp-2">{rp.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
